import { Box, Checkbox, Divider, Group, Stack, Text } from "@mantine/core";
import React, { ReactNode } from "react";
import { IReportChapterNode } from "@mcapps/shared/reportSchemas/kapitelSchema";

type Props = {
  titleNode?: ReactNode | null;
  chapters: IReportChapterNode[];
  onToggle: (chapterId: string, next: boolean) => void;
  renderLabel?: (node: IReportChapterNode) => React.ReactNode;
  isDisabled?: (node: IReportChapterNode) => boolean;
  indentPx?: number;
  showTopLevelDividers?: boolean;

  /**
   * Om true: när man togglar en parent så togglar man alla descendants också.
   * OBS: den här komponenten anropar onToggle för varje node (id, next).
   * Vill du göra detta atomiskt i redux: sätt false och hantera i thunk istället.
   */
  toggleDescendantsOnParentToggle?: boolean;

  /**
   * Om true: döljer children helt om ingen descendant är ibockad.
   * (Parent-raden syns alltid.)
   */
  hideEmptyChildren?: boolean;
};

const collectIdsDepthFirst = (node: IReportChapterNode): string[] => {
  const ids: string[] = [node.id];
  if (node.children?.length) {
    for (const c of node.children) ids.push(...collectIdsDepthFirst(c));
  }
  return ids;
};

const anyActiveInSubtree = (node: IReportChapterNode): boolean => {
  if (node.isActive) return true;
  if (!node.children?.length) return false;
  return node.children.some(anyActiveInSubtree);
};

export const ReportChaptersTree: React.FC<Props> = ({
  titleNode,
  chapters,
  onToggle,
  renderLabel,
  isDisabled,
  indentPx = 34,
  showTopLevelDividers = true,
  toggleDescendantsOnParentToggle = false,
  hideEmptyChildren = false,
}) => {
  const labelFn =
    renderLabel ??
    ((node: IReportChapterNode) => (
      <Text fw={600} size="sm">
        {node.text ? node.text : node.title}
      </Text>
    ));

  const handleToggle = (node: IReportChapterNode, next: boolean) => {
    if (toggleDescendantsOnParentToggle && node.children?.length) {
      const ids = collectIdsDepthFirst(node);
      ids.forEach((id) => onToggle(id, next));
      return;
    }

    onToggle(node.id, next);
  };

  const renderNode = (node: IReportChapterNode, level: number) => {
    const disabled = isDisabled?.(node) ?? false;

    // ✅ 2-state: UI följer bara node.isActive
    const checked = Boolean(node.isActive);
    const next = !checked;

    const hasChildren = Boolean(node.children?.length);

    // hideEmptyChildren: visa bara children om minst en descendant är aktiv
    const showChildren =
      hasChildren && (!hideEmptyChildren || node.isActive || node.children!.some(anyActiveInSubtree));

    return (
      <Stack key={node.id} gap={6}>
        <Box pl={level * indentPx}>
          <Checkbox
            size="sm"
            checked={checked}
            disabled={disabled}
            onChange={() => handleToggle(node, next)}
            label={labelFn(node)}
          />
        </Box>

        {showChildren ? <Stack gap={4}>{node.children!.map((c) => renderNode(c, level + 1))}</Stack> : null}
      </Stack>
    );
  };

  return (
    <Stack gap="xs">
      <Group align="center" justify="center">
        <Box style={{ flex: 1 }}>{titleNode}</Box>
      </Group>

      <Stack gap="xs">
        {chapters.map((ch, idx) => (
          <React.Fragment key={ch.id}>
            {idx > 0 && showTopLevelDividers && <Divider mt={0} mb="xs" />}
            {renderNode(ch, 0)}
          </React.Fragment>
        ))}
      </Stack>
    </Stack>
  );
};
